import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, X, Moon, Sun, Globe, ChevronDown, ChevronRight, 
  Check, ArrowRight, Star, Zap, Shield, Package, Truck, 
  BarChart3, DollarSign, Users, Building2, Phone, Mail, MapPin,
  Twitter, Linkedin, Github, Youtube, Facebook,
  Box, Warehouse, ClipboardList, Receipt, Calculator, Layers,
  Code, Database, Cloud, Lock, Settings, Heart, MessageSquare,
  HelpCircle, BookOpen, FileText, Briefcase, Target, Award,
  Clock, Calendar, Bell, Search, Home, Play, Headphones, AlertCircle
} from 'lucide-react';

const API_URL = API_BASE;

// Icon mapping for dynamic rendering from CMS
const iconMap = {
  Package, Truck, BarChart3, DollarSign, Users, Building2, Shield, Zap, Star,
  Box, Warehouse, ClipboardList, Receipt, Calculator, Check, ArrowRight, Layers,
  Code, Database, Cloud, Lock, Settings, Heart, MessageSquare, Globe,
  HelpCircle, BookOpen, FileText, Briefcase, Target, Award, Menu,
  Clock, Calendar, Bell, Search, Home, Play, Headphones, Mail, Phone, MapPin
};

// Get icon component by name
const getIcon = (iconName) => {
  if (!iconName) return Package;
  return iconMap[iconName] || Package;
};

// Public Landing Page Component - FULLY CMS DRIVEN
export const PublicLandingPage = () => {
  const [siteConfig, setSiteConfig] = useState(null);
  const [sections, setSections] = useState([]);
  const [navigation, setNavigation] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('en');
  const [menuOpen, setMenuOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);

  const [themeVariant, setThemeVariant] = useState(null);

  // Language labels - this is UI chrome, not content
  const languageLabels = {
    'en': { flag: '🇺🇸', name: 'English' },
    'pt-br': { flag: '🇧🇷', name: 'Português' },
    'es': { flag: '🇪🇸', name: 'Español' }
  };

  // Fetch published landing page configuration from CMS
  const fetchLandingData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch published config - translations are applied server-side based on lang parameter
      const response = await fetch(`${API_URL}/api/landing/public/default/config?lang=${language}`);
      const data = await response.json();
      
      if (data.status === 'published' && data.config) {
        setSiteConfig(data.config);
        setSections(data.sections || []);
        setNavigation(data.navigation || []);
        setThemeVariant(data.theme_variant || null);
      } else if (data.status === 'default') {
        // Fallback to template config
        const templateConfig = data.config || {};
        setSiteConfig(templateConfig.site || templateConfig);
        setSections(templateConfig.sections || []);
        setNavigation([]);
        setThemeVariant(data.theme_variant || null);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch landing config:', err);
      setError('Failed to load landing page configuration');
    } finally {
      setLoading(false);
    }
  }, [language]);

  // Generate dynamic CSS overrides for active skin/variant
  useEffect(() => {
    if (!themeVariant) {
      const tag = document.getElementById('landing-skin-styles');
      if (tag) tag.remove();
      return;
    }
    const colors = themeVariant.manifest?.theme?.colors || {};
    const assets = themeVariant.manifest?.theme?.assets || {};
    
    let css = `
      :root {
        --landing-primary: ${colors.primary || '#3B82F6'};
        --landing-primary-dark: ${colors.primaryDark || '#2563EB'};
        --landing-navbar-pill: ${colors.navbarPill || 'transparent'};
        --landing-login-btn: ${colors.loginButton || '#065f46'};
      }
      .bg-blue-600 {
        background-color: var(--landing-primary) !important;
      }
      .hover\\:bg-blue-700:hover {
        background-color: var(--landing-primary-dark) !important;
      }
      .text-blue-500 {
        color: var(--landing-primary) !important;
      }
      .bg-blue-500\\/10 {
        background-color: color-mix(in srgb, var(--landing-primary) 10%, transparent) !important;
      }
      .border-blue-500 {
        border-color: var(--landing-primary) !important;
      }
      .hover\\:border-blue-500:hover {
        border-color: var(--landing-primary) !important;
      }
      .from-blue-500 {
        --tw-gradient-from: var(--landing-primary) !important;
      }
      .from-blue-600 {
        --tw-gradient-from: var(--landing-primary) !important;
      }
      .to-blue-700 {
        --tw-gradient-to: var(--landing-primary-dark) !important;
      }
      .bg-gradient-to-r {
        background-image: linear-gradient(to right, var(--tw-gradient-stops)) !important;
      }
      .bg-gradient-to-b {
        background-image: linear-gradient(to bottom, var(--tw-gradient-stops)) !important;
      }
    `;
    
    if (assets.background) {
      const bgUrl = `${API_URL}${assets.background}`.replace(/\/+/g, '/').replace(':/', '://');
      css += `
        :root {
          --landing-bg-image: url("${bgUrl}");
        }
      `;
    }
    
    let styleTag = document.getElementById('landing-skin-styles');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'landing-skin-styles';
      document.head.appendChild(styleTag);
    }
    styleTag.innerHTML = css;
    
    return () => {
      const tag = document.getElementById('landing-skin-styles');
      if (tag) tag.remove();
    };
  }, [themeVariant]);

  useEffect(() => {
    fetchLandingData();
  }, [fetchLandingData]);

  // Toggle theme
  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  // Theme classes derived from site config
  const isDark = theme === 'dark';
  const primaryColor = siteConfig?.primary_color || '#3B82F6';
  const bgClass = isDark ? 'bg-slate-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-slate-900';
  const mutedClass = isDark ? 'text-slate-400' : 'text-slate-600';
  const cardBg = isDark ? 'bg-slate-800/50' : 'bg-slate-100';
  const borderClass = isDark ? 'border-slate-700' : 'border-slate-200';

  // Get section by key
  const getSection = (key) => sections.find(s => s.section_key === key);
  
  // Get blocks for a section
  const getBlocks = (sectionKey) => {
    const section = getSection(sectionKey);
    return (section?.content_blocks || []).filter(b => b.is_visible !== false);
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !siteConfig) {
    return (
      <div className={`min-h-screen ${bgClass} flex items-center justify-center`}>
        <div className="text-center">
          <h1 className={`text-2xl font-bold ${textClass} mb-4`}>Landing Page Not Configured</h1>
          <p className={mutedClass}>Please configure your landing page in the admin panel.</p>
          <a href="/admin/landing" className="mt-4 inline-block px-6 py-3 bg-blue-600 text-white rounded-lg">
            Go to Admin
          </a>
        </div>
      </div>
    );
  }

  const siteName = siteConfig.site_name || 'EzERP';
  const tagline = siteConfig.tagline || '';
  const enabledLanguages = siteConfig.enabled_languages || ['en'];

  const navbarPillColor = themeVariant?.manifest?.theme?.colors?.navbarPill;
  const isPillNavbar = !!navbarPillColor;

  // Sections that should NEVER appear as navbar links
  const NAV_EXCLUDED = new Set(['hero', 'footer', 'cta']);

  // Human-readable labels for each section_key
  const NAV_LABELS = {
    features:     language === 'pt-br' ? 'Recursos'   : language === 'es' ? 'Funciones'  : 'Features',
    modules:      language === 'pt-br' ? 'Módulos'    : language === 'es' ? 'Módulos'    : 'Modules',
    pricing:      language === 'pt-br' ? 'Preços'     : language === 'es' ? 'Precios'    : 'Pricing',
    faq:          'FAQ',
    stats:        language === 'pt-br' ? 'Números'    : language === 'es' ? 'Cifras'     : 'Stats',
    testimonials: language === 'pt-br' ? 'Clientes'   : language === 'es' ? 'Clientes'   : 'Customers',
    contact:      language === 'pt-br' ? 'Contato'    : language === 'es' ? 'Contacto'   : 'Contact',
  };

  // Only show sections that: are visible, are navigable, and have content
  const navSections = sections.filter(
    s => s.is_visible
      && !NAV_EXCLUDED.has(s.section_key)
      && (s.content_blocks?.length > 0 || NAV_LABELS[s.section_key])
  );

  return (
    <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
      {/* Navigation - Dynamic from CMS */}
      <nav 
        className={isPillNavbar 
          ? `fixed top-4 left-1/2 -translate-x-1/2 w-[92%] max-w-6xl z-50 rounded-full border border-slate-700/80 shadow-lg px-6 transition-all duration-350`
          : `fixed top-0 left-0 right-0 z-50 ${isDark ? 'bg-slate-900/90' : 'bg-white/90'} backdrop-blur-lg border-b ${borderClass} transition-all duration-350`
        }
        style={isPillNavbar ? {
          backgroundColor: navbarPillColor,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        } : {}}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              {siteConfig.logo_url ? (
                <img src={isDark ? (siteConfig.logo_dark_url || siteConfig.logo_url) : siteConfig.logo_url} alt={siteName} className="h-8" />
              ) : (
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Package className="w-5 h-5 text-white" />
                </div>
              )}
              <span className="text-xl font-bold">{siteName}</span>
            </div>

            {/* Desktop Navigation - only real navigable sections with proper labels */}
            <div className="hidden md:flex items-center gap-6">
              {navSections.map((section) => (
                <a
                  key={section.section_key}
                  href={`#${section.section_key}`}
                  className={`text-sm font-medium ${mutedClass} hover:${textClass} transition-colors`}
                >
                  {NAV_LABELS[section.section_key] || section.section_key}
                </a>
              ))}
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {/* Language Selector - Only show if multiple languages enabled */}
              {enabledLanguages.length > 1 && (
                <div className="relative">
                  <button
                    onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg ${cardBg} ${mutedClass} hover:${textClass} transition-colors`}
                    data-testid="language-selector"
                  >
                    <span>{languageLabels[language]?.flag}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {langDropdownOpen && (
                    <div className={`absolute right-0 mt-2 w-40 ${isDark ? 'bg-slate-800' : 'bg-white'} rounded-lg shadow-xl border ${borderClass} overflow-hidden z-50`}>
                      {enabledLanguages.map((langCode) => (
                        <button
                          key={langCode}
                          onClick={() => { setLanguage(langCode); setLangDropdownOpen(false); }}
                          className={`w-full px-4 py-2 text-left flex items-center gap-2 hover:bg-blue-500/10 ${language === langCode ? 'bg-blue-500/20 text-blue-500' : mutedClass}`}
                          data-testid={`lang-option-${langCode}`}
                        >
                          <span>{languageLabels[langCode]?.flag}</span>
                          <span>{languageLabels[langCode]?.name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2 rounded-lg ${cardBg} ${mutedClass} hover:${textClass} transition-colors`}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {/* CTA Button */}
              <a
                href="/login"
                className="hidden md:inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                data-testid="get-started-btn"
              >
                {language === 'pt-br' ? 'Começar' : language === 'es' ? 'Empezar' : 'Get Started'}
                <ArrowRight className="w-4 h-4" />
              </a>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="md:hidden p-2 rounded-lg"
              >
                {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden ${isDark ? 'bg-slate-800' : 'bg-slate-50'} border-t ${borderClass}`}
            >
              <div className="px-4 py-4 space-y-2">
                {navSections.map((section) => (
                  <a
                    key={section.section_key}
                    href={`#${section.section_key}`}
                    className={`block py-2 text-sm font-medium ${mutedClass}`}
                    onClick={() => setMenuOpen(false)}
                  >
                    {NAV_LABELS[section.section_key] || section.section_key}
                  </a>
                ))}
                <a href="/login" className="block py-2 px-4 bg-blue-600 text-white text-center rounded-lg mt-4">
                  {language === 'pt-br' ? 'Começar' : language === 'es' ? 'Empezar' : 'Get Started'}
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* Dynamic Sections Renderer */}
      <div className="pt-16">
        {sections
          .filter(section => section.is_visible)
          .sort((a, b) => a.order_index - b.order_index)
          .map((section) => (
            <DynamicSection
              key={section.id}
              section={section}
              siteConfig={siteConfig}
              isDark={isDark}
              bgClass={bgClass}
              textClass={textClass}
              mutedClass={mutedClass}
              cardBg={cardBg}
              borderClass={borderClass}
              language={language}
              themeVariant={themeVariant}
            />
          ))}
      </div>

      {/* Floating Demo Credentials */}
      {themeVariant?.show_demo_credentials && (
        <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-slate-900/90 border border-slate-700 rounded-xl p-4 shadow-2xl backdrop-blur-md">
          <div className="flex items-center gap-2 mb-2 text-pink-500 font-bold text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>Acesso de Demonstração</span>
          </div>
          <p className="text-xs text-slate-400 mb-3">
            Utilize as credenciais abaixo para testar o sistema EzERP:
          </p>
          <div className="space-y-1.5 font-mono text-xs">
            <div className="flex justify-between text-slate-300">
              <span>Admin:</span>
              <span className="text-white">admin@ezerp.com / admin123</span>
            </div>
            <div className="flex justify-between text-slate-350">
              <span>Gerente:</span>
              <span className="text-white">manager@ezerp.com / manager123</span>
            </div>
          </div>
        </div>
      )}

      {/* Footer Section - rendered if exists */}
      {getSection('footer') && (
        <FooterSection
          section={getSection('footer')}
          siteConfig={siteConfig}
          isDark={isDark}
          mutedClass={mutedClass}
          borderClass={borderClass}
          language={language}
        />
      )}
    </div>
  );
};

// Dynamic Section Renderer - renders based on section_type
const DynamicSection = ({ section, siteConfig, isDark, bgClass, textClass, mutedClass, cardBg, borderClass, language, themeVariant }) => {
  const blocks = (section.content_blocks || []).filter(b => b.is_visible !== false).sort((a, b) => a.order_index - b.order_index);
  
  // Determine background
  let sectionBg = '';
  if (section.background_type === 'solid' && section.background_value) {
    sectionBg = `bg-[${section.background_value}]`;
  } else if (section.background_type === 'gradient') {
    sectionBg = section.background_value || '';
  } else {
    // Alternate backgrounds for visual interest
    sectionBg = section.order_index % 2 === 0 ? '' : (isDark ? 'bg-slate-800/30' : 'bg-slate-50');
  }

  const sectionId = section.section_key;

  switch (section.section_type) {
    case 'hero':
      return <HeroSection id={sectionId} blocks={blocks} siteConfig={siteConfig} isDark={isDark} bgClass={bgClass} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} language={language} sectionBg={sectionBg} themeVariant={themeVariant} />;
    case 'features':
      return <FeaturesSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} config={section.config} />;
    case 'modules':
      return <ModulesSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} config={section.config} />;
    case 'pricing':
      return <PricingSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} language={language} />;
    case 'faq':
      return <FAQSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'cta':
      return <CTASection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} language={language} />;
    case 'stats':
      return <StatsSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'testimonials':
      return <TestimonialsSection id={sectionId} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
    case 'footer':
      return null; // Footer is rendered separately
    default:
      return <GenericSection id={sectionId} section={section} blocks={blocks} isDark={isDark} textClass={textClass} mutedClass={mutedClass} cardBg={cardBg} borderClass={borderClass} sectionBg={sectionBg} />;
  }
};

// Hero Section - Dynamic
const HeroSection = ({ id, blocks, siteConfig, isDark, bgClass, textClass, mutedClass, cardBg, borderClass, language, sectionBg, themeVariant }) => {
  const heroBlock = blocks[0] || {};
  const statsBlocks = blocks.filter(b => b.block_type === 'stat');
  const hasBgImage = !!themeVariant?.manifest?.theme?.assets?.background;
  
  return (
    <section 
      id={id} 
      className={`pt-32 pb-20 px-4 relative overflow-hidden flex flex-col items-center justify-center min-h-[80vh] ${sectionBg}`}
      style={hasBgImage ? {
        backgroundImage: `var(--landing-bg-image)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      } : {}}
    >
      {/* Dark Overlay for readability */}
      {hasBgImage && (
        <div className="absolute inset-0 bg-slate-950/65 backdrop-blur-[2px] z-0"></div>
      )}
      
      <div className="max-w-7xl mx-auto text-center relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {heroBlock.subtitle && (
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 text-blue-500 text-sm font-medium mb-6">
              <Zap className="w-4 h-4" />
              {heroBlock.subtitle}
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            {heroBlock.title || siteConfig?.tagline || 'Welcome'}
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
              {siteConfig?.site_name || 'EzERP'}
            </span>
          </h1>
          {heroBlock.description && (
            <p className={`text-lg sm:text-xl ${mutedClass} max-w-3xl mx-auto mb-8`}>
              {heroBlock.description}
            </p>
          )}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {heroBlock.cta_text && (
              <a
                href={heroBlock.cta_url || '/signup'}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-lg transition-all hover:scale-105"
              >
                {heroBlock.cta_text}
                <ArrowRight className="w-5 h-5" />
              </a>
            )}
            {heroBlock.data?.secondary_cta_text && (
              <a
                href={heroBlock.data?.secondary_cta_url || '#demo'}
                className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 ${cardBg} ${textClass} rounded-xl font-semibold text-lg border ${borderClass} hover:border-blue-500 transition-all`}
              >
                {heroBlock.data.secondary_cta_text}
              </a>
            )}
          </div>
        </motion.div>

        {/* Stats - if available */}
        {statsBlocks.length > 0 && (
          <div className={`mt-16 grid grid-cols-2 md:grid-cols-${Math.min(statsBlocks.length, 4)} gap-6 ${cardBg} rounded-2xl p-8 border ${borderClass}`}>
            {statsBlocks.map((stat, index) => (
              <div key={stat.id || index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-500">{stat.title}</div>
                <div className={`text-sm ${mutedClass}`}>{stat.description}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Features Section - Dynamic
const FeaturesSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, config }) => {
  const columns = config?.columns || 3;
  const title = config?.title || 'Features';
  const subtitle = config?.subtitle || '';
  
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{title}</h2>
          {subtitle && <p className={`text-lg ${mutedClass} max-w-2xl mx-auto`}>{subtitle}</p>}
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${columns} gap-6`}>
          {blocks.map((feature, index) => {
            const IconComponent = getIcon(feature.icon);
            return (
              <motion.div
                key={feature.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-2xl ${cardBg} border ${borderClass} hover:border-blue-500/50 transition-all group`}
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <IconComponent className="w-6 h-6 text-blue-500" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className={mutedClass}>{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Modules Section - Dynamic
const ModulesSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, config }) => {
  const title = config?.title || 'Modules';
  const subtitle = config?.subtitle || '';
  
  if (blocks.length === 0) return null;
  
  const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 'bg-pink-500', 'bg-cyan-500', 'bg-red-500', 'bg-yellow-500'];
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{title}</h2>
          {subtitle && <p className={`text-lg ${mutedClass} max-w-2xl mx-auto`}>{subtitle}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {blocks.map((module, index) => {
            const IconComponent = getIcon(module.icon);
            const colorClass = module.data?.color || colors[index % colors.length];
            return (
              <motion.div
                key={module.id || index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`p-6 rounded-2xl ${cardBg} border ${borderClass} hover:border-blue-500/50 transition-all text-center`}
              >
                <div className={`w-16 h-16 rounded-xl ${colorClass} flex items-center justify-center mx-auto mb-4`}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2">{module.title}</h3>
                <p className={`text-sm ${mutedClass}`}>{module.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Pricing Section - Dynamic
const PricingSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, language }) => {
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {language === 'pt-br' ? 'Planos e Preços' : language === 'es' ? 'Planes y Precios' : 'Pricing Plans'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {blocks.map((plan, index) => {
            const isPopular = plan.data?.popular || index === 1;
            const features = plan.data?.features || [];
            const price = plan.data?.price || plan.title;
            const currency = plan.data?.currency || '$';
            const period = plan.data?.period || (language === 'pt-br' ? '/mês' : language === 'es' ? '/mes' : '/month');
            
            return (
              <motion.div
                key={plan.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative p-8 rounded-2xl ${isPopular ? 'bg-gradient-to-b from-blue-600 to-blue-700 text-white' : `${cardBg} border ${borderClass}`}`}
              >
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-yellow-500 text-slate-900 text-sm font-semibold rounded-full">
                    {language === 'pt-br' ? 'Mais Popular' : language === 'es' ? 'Más Popular' : 'Most Popular'}
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2">{plan.title}</h3>
                <p className={isPopular ? 'text-blue-100' : mutedClass}>{plan.subtitle}</p>
                <div className="my-6">
                  <span className="text-4xl font-bold">{price === 'Custom' ? price : `${currency}${price}`}</span>
                  {price !== 'Custom' && <span className={isPopular ? 'text-blue-100' : mutedClass}>{period}</span>}
                </div>
                {features.length > 0 && (
                  <ul className="space-y-3 mb-8">
                    {features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Check className={`w-5 h-5 ${isPopular ? 'text-green-300' : 'text-green-500'}`} />
                        <span className={isPopular ? '' : mutedClass}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {plan.cta_text && (
                  <a
                    href={plan.cta_url || '/signup'}
                    className={`block w-full py-3 rounded-xl font-semibold text-center transition-all ${
                      isPopular 
                        ? 'bg-white text-blue-600 hover:bg-blue-50' 
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {plan.cta_text}
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// FAQ Section - Dynamic
const FAQSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  const [openIndex, setOpenIndex] = useState(null);
  
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">FAQ</h2>
        </div>

        <div className="space-y-4">
          {blocks.map((faq, index) => (
            <div key={faq.id || index} className={`rounded-xl ${cardBg} border ${borderClass} overflow-hidden`}>
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left"
              >
                <span className="font-medium">{faq.title}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${openIndex === index ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className={`px-6 pb-4 ${mutedClass}`}>{faq.description}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section - Dynamic
const CTASection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg, language }) => {
  const ctaBlock = blocks[0] || {};
  
  return (
    <section id={id} className={`py-20 px-4 ${isDark ? 'bg-gradient-to-r from-blue-900/50 to-purple-900/50' : 'bg-gradient-to-r from-blue-50 to-purple-50'}`}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl sm:text-4xl font-bold mb-4">{ctaBlock.title || 'Get Started Today'}</h2>
        {ctaBlock.description && <p className={`text-lg ${mutedClass} mb-8`}>{ctaBlock.description}</p>}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {ctaBlock.cta_text && (
            <a
              href={ctaBlock.cta_url || '/signup'}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-lg transition-all"
            >
              {ctaBlock.cta_text}
              <ArrowRight className="w-5 h-5" />
            </a>
          )}
          {ctaBlock.data?.secondary_cta_text && (
            <a
              href={ctaBlock.data?.secondary_cta_url || '/contact'}
              className={`w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 ${cardBg} rounded-xl font-semibold text-lg border ${borderClass}`}
            >
              {ctaBlock.data.secondary_cta_text}
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

// Stats Section - Dynamic
const StatsSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(blocks.length, 4)} gap-6 ${cardBg} rounded-2xl p-8 border ${borderClass}`}>
          {blocks.map((stat, index) => (
            <div key={stat.id || index} className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-blue-500">{stat.title}</div>
              <div className={`text-sm ${mutedClass}`}>{stat.description}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - Dynamic
const TestimonialsSection = ({ id, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  if (blocks.length === 0) return null;
  
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Testimonials</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blocks.map((testimonial, index) => (
            <motion.div
              key={testimonial.id || index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`p-6 rounded-2xl ${cardBg} border ${borderClass}`}
            >
              <div className="flex items-center gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-yellow-500 text-yellow-500" />
                ))}
              </div>
              <p className={`${mutedClass} mb-4`}>"{testimonial.description}"</p>
              <div className="flex items-center gap-3">
                {testimonial.image_url && (
                  <img src={testimonial.image_url} alt={testimonial.title} className="w-10 h-10 rounded-full" />
                )}
                <div>
                  <div className="font-semibold">{testimonial.title}</div>
                  <div className={`text-sm ${mutedClass}`}>{testimonial.subtitle}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Generic Section - For custom section types
const GenericSection = ({ id, section, blocks, isDark, textClass, mutedClass, cardBg, borderClass, sectionBg }) => {
  return (
    <section id={id} className={`py-20 px-4 ${sectionBg}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 capitalize">{section.section_key.replace(/_/g, ' ')}</h2>
        </div>
        {blocks.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {blocks.map((block, index) => (
              <div key={block.id || index} className={`p-6 rounded-2xl ${cardBg} border ${borderClass}`}>
                {block.title && <h3 className="text-xl font-semibold mb-2">{block.title}</h3>}
                {block.description && <p className={mutedClass}>{block.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// Footer Section - Dynamic
const FooterSection = ({ section, siteConfig, isDark, mutedClass, borderClass, language }) => {
  const blocks = (section?.content_blocks || []).filter(b => b.is_visible !== false);
  const socialLinks = siteConfig?.social_links || {};
  
  const socialIcons = {
    twitter: Twitter,
    linkedin: Linkedin,
    github: Github,
    youtube: Youtube,
    facebook: Facebook,
  };
  
  return (
    <footer className={`py-12 px-4 border-t ${borderClass}`}>
      <div className="max-w-7xl mx-auto">
        {blocks.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            {blocks.map((block, index) => (
              <div key={block.id || index}>
                <h4 className="font-semibold mb-4">{block.title}</h4>
                {block.data?.links && (
                  <ul className={`space-y-2 ${mutedClass}`}>
                    {block.data.links.map((link, i) => (
                      <li key={i}><a href={link.url} className="hover:text-blue-500">{link.label}</a></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
        
        <div className={`pt-8 border-t ${borderClass} flex flex-col md:flex-row items-center justify-between gap-4`}>
          <div className="flex items-center gap-2">
            <Package className="w-6 h-6 text-blue-500" />
            <span className="font-bold">{siteConfig?.site_name || 'EzERP'}</span>
          </div>
          <p className={`text-sm ${mutedClass}`}>
            © {new Date().getFullYear()} {siteConfig?.site_name || 'EzERP'}. {language === 'pt-br' ? 'Todos os direitos reservados.' : language === 'es' ? 'Todos los derechos reservados.' : 'All rights reserved.'}
          </p>
          <div className="flex gap-4">
            {Object.entries(socialLinks).map(([platform, url]) => {
              const Icon = socialIcons[platform.toLowerCase()];
              if (!Icon || !url) return null;
              return (
                <a key={platform} href={url} className={`${mutedClass} hover:text-blue-500`}>
                  <Icon className="w-5 h-5" />
                </a>
              );
            })}
          </div>
        </div>

        {/* Contact Info */}
        {(siteConfig?.contact_email || siteConfig?.contact_phone) && (
          <div className={`mt-4 flex flex-wrap justify-center gap-6 text-sm ${mutedClass}`}>
            {siteConfig.contact_email && (
              <a href={`mailto:${siteConfig.contact_email}`} className="flex items-center gap-1 hover:text-blue-500">
                <Mail className="w-4 h-4" />
                {siteConfig.contact_email}
              </a>
            )}
            {siteConfig.contact_phone && (
              <a href={`tel:${siteConfig.contact_phone}`} className="flex items-center gap-1 hover:text-blue-500">
                <Phone className="w-4 h-4" />
                {siteConfig.contact_phone}
              </a>
            )}
          </div>
        )}
      </div>
    </footer>
  );
};

export default PublicLandingPage;
